import React, {
  createContext,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { useDecision } from '@optimizely/react-sdk';
import { useTrackOptimizely } from '@fff-web/fff-analytics';
import defaultReactivateMyMembershipContext from './defaultReactivateMyMembershipContext';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../../utils/Optimizely/featureFlagVariations';
import useSelectedPlan from '../../hooks/useSelectedPlan';
import { useUserContext } from '@fff-web/fff-utilities';
import { useHistory, useLocation } from 'react-router-dom';
import { redirectToBaseSite } from '../../../utils/redirectMethods';

export const ReactivateMyMembershipContext = createContext(
  defaultReactivateMyMembershipContext
);

export const ReactivateMyMembershipProvider = ({
  children,
}: {
  children: React.ReactNode | React.ReactNodeArray;
}) => {
  const [btnContainerRef] = useState(useRef(null));
  const { setPlanCode, selectedPlanCode } = useSelectedPlan();
  const [couponsApplied, setCouponsApplied] = useState([]);
  const [showReactivationContent, setShowContent] = useState(true);
  const history = useHistory();
  const { search, pathname } = useLocation();
  const { userInfo } = useUserContext();

  const myAccountPath =
    pathname.indexOf('/my-account-v3') === -1 ? '' : '/my-account-v3';
  const subscriptionState = userInfo?.user?.subscriptionState;
  const loggedIn = userInfo?.loggedIn;

  if (loggedIn && subscriptionState === 'CANCELED') {
    history.replace(`${myAccountPath}/reactivate-today${search}`);
    return null;
  }

  if (loggedIn && subscriptionState !== 'EXPIRED') {
    redirectToBaseSite();
    return null;
  }
  const [growthMyMembershipDecision] = useDecision(
    GROWTH_MY_REACTIVATE_MEMBERSHIP.FEATURE_FLAG
  );

  useTrackOptimizely(
    growthMyMembershipDecision,
    GROWTH_MY_REACTIVATE_MEMBERSHIP.EXPERIMENT_NAME
  );

  useEffect(() => {
    if (growthMyMembershipDecision.enabled) {
      //This Avoid the flash of the content before Optimizely loads
      setShowContent(false);
    }

    if (
      !growthMyMembershipDecision.enabled &&
      growthMyMembershipDecision.variationKey ===
        GROWTH_MY_REACTIVATE_MEMBERSHIP.VARIATION_OFF
    ) {
      //When the experiment if off the values of enable if false so we need to check if the variation key is off as well
      setShowContent(false);
    }
  }, [growthMyMembershipDecision.enabled]);

  const memoizedValues = useMemo(
    () => ({
      growthMyMembership: growthMyMembershipDecision,
      setPlanCode,
      selectedPlanCode,
      couponsApplied,
      setCouponsApplied,
      myAccountPath,
      showReactivationContent,
    }),
    [
      growthMyMembershipDecision,
      selectedPlanCode,
      couponsApplied,
      setCouponsApplied,
      showReactivationContent,
    ]
  );

  return (
    <ReactivateMyMembershipContext.Provider
      value={{
        btnContainerRef,
        ...memoizedValues,
      }}
    >
      {children}
    </ReactivateMyMembershipContext.Provider>
  );
};

// This hook is the equivalent of a context consumer
// TO USE: const { btnContainerRef } = useReactivateMyMembershipContext();
// MAKE SURE: <ReactivateMyMembershipProvider> is a level up
export function useReactivateMyMembershipContext() {
  const context = React.useContext(ReactivateMyMembershipContext);
  if (!context) {
    throw new Error(
      'You must use the ReactivateMyMembershipProvider in order to consume this context.'
    );
  }
  return context;
}

export default ReactivateMyMembershipProvider;
